import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Badge,
    Box,
    Flex,
    HStack,
    Stack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
} from '@/components/v4';
import {
    DR_TRUNK_TYPE_NAME,
    FOOTER_BUTTON_WIDTH,
    OC_MOBILE_USAGE,
    OC_TRUNK_TYPE_NAME,
    TRUNK_KINDS,
    fixedModalHeight,
} from '../constants';
import {
    MESSAGES,
    OVERSTAMP_MODES,
    OVERSTAMP_PATTERN,
    TRUNK_CHOICES,
    UNCHANGED,
    buildLookups,
    getBulkUpdatePlan,
    getInitialChoices,
    getOverstampMode,
    getTrunkBreakdown,
} from '../utils/bulkUpdate';
import { digitsOnly, getTrunkKind } from '../utils/rows';
import TrunkOptionCards from './TrunkOptionCards';
import WhatWillChange from './WhatWillChange';

const INFO_REASONS = [MESSAGES.nothingPicked, MESSAGES.alreadyMatches];

const getFooterStatus = (reason) => {
    if (!reason) return 'success';
    return INFO_REASONS.includes(reason) ? 'info' : 'error';
};

// Keeps a leading + and digits only, so the field always reads as E.164.
const sanitizeOverstamp = (value) =>
    value.trim() ? `+${digitsOnly(value)}` : '';

const Step = ({ number, title, note, hint, children }) => (
    <Box>
        <HStack spacing={2} mb={hint ? 1 : 2}>
            <Flex
                boxSize="22px"
                flexShrink={0}
                borderRadius="full"
                bg="brand.500"
                color="white"
                fontSize="xs"
                fontWeight="bold"
                align="center"
                justify="center">
                {number}
            </Flex>
            <Text fontWeight="semibold">{title}</Text>
            {note && (
                <Text fontSize="sm" color="red.500">
                    · {note}
                </Text>
            )}
        </HStack>
        {hint && (
            <Text fontSize="sm" color="gray.500" mb={2}>
                {hint}
            </Text>
        )}
        {children}
    </Box>
);

const BREAKDOWN = [
    { key: 'oc', label: OC_TRUNK_TYPE_NAME, colorScheme: 'blue' },
    { key: 'dr', label: DR_TRUNK_TYPE_NAME, colorScheme: 'cyan' },
    { key: 'other', label: 'Other trunk', colorScheme: 'purple' },
    { key: 'none', label: 'No trunk', colorScheme: 'gray' },
];

/** Options for a required-or-unchanged select. */
const withUnchanged = (options, isRequired) =>
    isRequired
        ? options
        : [{ value: UNCHANGED, label: 'Unchanged' }, ...options];

const BulkUpdateModal = ({
    isOpen,
    onClose,
    rows,
    trunks,
    usages,
    tenant,
    onApply,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [choices, setChoices] = useState(() => getInitialChoices(trunks));

    // Every open starts from "Keep current" and "Unchanged".
    useEffect(() => {
        if (isOpen) setChoices(getInitialChoices(trunks));
    }, [isOpen, trunks]);

    const lookups = useMemo(
        () => buildLookups({ trunks, usages }),
        [trunks, usages],
    );
    const plan = useMemo(
        () => getBulkUpdatePlan(rows, choices, lookups),
        [rows, choices, lookups],
    );
    const { requirements } = plan;
    const breakdown = useMemo(() => getTrunkBreakdown(rows), [rows]);

    const ocTrunks = useMemo(
        () => trunks.filter((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.OC),
        [trunks],
    );
    const drTrunks = useMemo(
        () => trunks.filter((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.DR),
        [trunks],
    );

    // Calling profiles belong to Operator Connect trunks.
    const profileOptions = useMemo(() => {
        const sourceTrunks =
            choices.trunk === TRUNK_CHOICES.OC
                ? ocTrunks.filter((trunk) => trunk.id === choices.ocTrunkID)
                : ocTrunks;
        const profiles = new Map();
        sourceTrunks
            .flatMap((trunk) => trunk.callingProfileList ?? [])
            .forEach((profile) => profiles.set(profile.id, profile));
        return [...profiles.values()].map((profile) => ({
            value: profile.id,
            label: profile.name,
        }));
    }, [choices.trunk, choices.ocTrunkID, ocTrunks]);

    // Teams Phone Mobile has its own flow, so it is not offered here.
    const usageOptions = useMemo(
        () =>
            usages
                .filter((usage) => usage.id !== OC_MOBILE_USAGE)
                .map((usage) => ({
                    value: usage.id,
                    label: usage.displayName ?? usage.id,
                })),
        [usages],
    );

    const update = (changes) =>
        setChoices((current) => ({ ...current, ...changes }));

    const overstampMode = getOverstampMode(choices, requirements);
    const overstampOptions = [
        ...(requirements.overstampUnchangedAllowed
            ? [{ value: OVERSTAMP_MODES.UNCHANGED, label: 'Unchanged' }]
            : []),
        {
            value: OVERSTAMP_MODES.DEFAULT,
            label: 'Default (use the number itself)',
        },
        { value: OVERSTAMP_MODES.CUSTOM, label: 'Select' },
    ];
    const isOverstampInvalid =
        !!choices.overstampValue &&
        !OVERSTAMP_PATTERN.test(choices.overstampValue);

    const ocFieldValue = (choice, isRequired) =>
        isRequired && choice === UNCHANGED ? '' : choice;

    const renderTrunkPicker = (label, list, field) =>
        list.length > 1 && (
            <FormControl mt={3}>
                <FormLabel fontSize="sm">{label}</FormLabel>
                <Select
                    size="sm"
                    value={choices[field] ?? ''}
                    onChange={(e) => update({ [field]: e.target.value })}
                    options={list.map((trunk) => ({
                        value: trunk.id,
                        label: trunk.name,
                    }))}
                />
            </FormControl>
        );

    const overstampStep = requirements.showOcSettings ? 3 : 2;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" closeOnOverlayClick>
            <ModalOverlay />
            <ModalContent
                bg={darkMode ? 'gray.800' : 'white'}
                sx={fixedModalHeight('760px')}>
                <ModalHeader color={darkMode ? 'white' : 'gray.900'}>
                    Bulk Update Selected Numbers
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody overflowY="auto">
                    <Stack spacing={5}>
                        <Wrap spacing={2} align="center">
                            <WrapItem>
                                <Text fontSize="sm" color="gray.500">
                                    Currently selected:
                                </Text>
                            </WrapItem>
                            {BREAKDOWN.filter(
                                ({ key }) => breakdown[key] > 0,
                            ).map(({ key, label, colorScheme }) => (
                                <WrapItem key={key}>
                                    <Badge
                                        borderRadius="full"
                                        px={2}
                                        textTransform="none"
                                        colorScheme={colorScheme}>
                                        {breakdown[key].toLocaleString()}{' '}
                                        {label}
                                    </Badge>
                                </WrapItem>
                            ))}
                        </Wrap>

                        <Step number={1} title="Trunk">
                            <TrunkOptionCards
                                tenant={tenant}
                                value={choices.trunk}
                                onChange={(trunk) => update({ trunk })}
                            />
                            {choices.trunk === TRUNK_CHOICES.OC &&
                                renderTrunkPicker(
                                    'Operator Connect trunk',
                                    ocTrunks,
                                    'ocTrunkID',
                                )}
                            {choices.trunk === TRUNK_CHOICES.DR &&
                                renderTrunkPicker(
                                    'Direct Routing trunk',
                                    drTrunks,
                                    'drTrunkID',
                                )}
                        </Step>

                        {requirements.showOcSettings && (
                            <Step
                                number={2}
                                title="Operator Connect settings"
                                note={
                                    requirements.profileRequired ||
                                    requirements.usageRequired
                                        ? 'required'
                                        : null
                                }>
                                <Stack
                                    direction={{ base: 'column', md: 'row' }}
                                    spacing={3}>
                                    <FormControl>
                                        <FormLabel fontSize="sm">
                                            Calling profile
                                        </FormLabel>
                                        <Select
                                            size="sm"
                                            placeholder={
                                                requirements.profileRequired
                                                    ? 'Choose…'
                                                    : undefined
                                            }
                                            value={ocFieldValue(
                                                choices.callingProfileID,
                                                requirements.profileRequired,
                                            )}
                                            onChange={(e) =>
                                                update({
                                                    callingProfileID:
                                                        e.target.value ||
                                                        UNCHANGED,
                                                })
                                            }
                                            options={withUnchanged(
                                                profileOptions,
                                                requirements.profileRequired,
                                            )}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="sm">
                                            Usage type
                                        </FormLabel>
                                        <Select
                                            size="sm"
                                            placeholder={
                                                requirements.usageRequired
                                                    ? 'Choose…'
                                                    : undefined
                                            }
                                            value={ocFieldValue(
                                                choices.usageID,
                                                requirements.usageRequired,
                                            )}
                                            onChange={(e) =>
                                                update({
                                                    usageID:
                                                        e.target.value ||
                                                        UNCHANGED,
                                                })
                                            }
                                            options={withUnchanged(
                                                usageOptions,
                                                requirements.usageRequired,
                                            )}
                                        />
                                    </FormControl>
                                </Stack>
                            </Step>
                        )}

                        {requirements.showOverstamp && (
                            <Step
                                number={overstampStep}
                                title="Overstamp"
                                hint="Leave as Default to use the original value.">
                                <Select
                                    size="sm"
                                    aria-label="Overstamp"
                                    value={overstampMode}
                                    onChange={(e) =>
                                        update({
                                            overstampMode: e.target.value,
                                        })
                                    }
                                    options={overstampOptions}
                                />
                                {overstampMode === OVERSTAMP_MODES.CUSTOM && (
                                    <FormControl
                                        isInvalid={isOverstampInvalid}
                                        mt={2}>
                                        <Input
                                            size="sm"
                                            aria-label="Overstamp number"
                                            placeholder="+441234567890"
                                            inputMode="tel"
                                            value={choices.overstampValue}
                                            onChange={(e) =>
                                                update({
                                                    overstampValue:
                                                        sanitizeOverstamp(
                                                            e.target.value,
                                                        ),
                                                })
                                            }
                                        />
                                        <FormErrorMessage>
                                            Enter a number starting with +
                                            followed by 6–15 digits
                                        </FormErrorMessage>
                                    </FormControl>
                                )}
                            </Step>
                        )}

                        <WhatWillChange
                            changing={plan.changing}
                            changingCount={plan.changingCount}
                            unchangedCount={plan.unchangedCount}
                        />
                    </Stack>
                </ModalBody>
                {/* The footer always shows one message line, either why Apply
                    is disabled or what it will do, so it never changes height
                    and stays in view however far the body is scrolled */}
                <ModalFooter
                    flexDirection="column"
                    alignItems="stretch"
                    gap={3}>
                    <Alert py={2} status={getFooterStatus(plan.reason)}>
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                            {plan.reason ??
                                `${plan.changingCount.toLocaleString()} ${
                                    plan.changingCount === 1
                                        ? 'number'
                                        : 'numbers'
                                } will be updated`}
                        </AlertDescription>
                    </Alert>
                    <HStack spacing={3}>
                        <Button
                            isDisabled={!plan.canApply}
                            onClick={() => onApply(plan.items)}>
                            Apply Updates to Selected
                        </Button>
                        <Button
                            variant="outline"
                            minW={FOOTER_BUTTON_WIDTH}
                            onClick={onClose}>
                            Cancel
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default BulkUpdateModal;
